// Pops the floating cart receipt open once per visit when the shopper looks like they're leaving:
// on desktop, the pointer leaving through the top of the window (toward tabs or the address bar);
// on touch screens, a quick upward flick after scrolling down the page (reaching for the browser bar).
// The slip itself opens the receipt any time via its show-popover button; this only adds the exit nudge.
const FLICK_DISTANCE = 140;
const FLICK_TIME = 200;
const ARM_DELAY = 4000;

class ReceiptBug extends HTMLElement {
  #abort = null;
  #armed = false;
  #armTimer = 0;
  #lastY = 0;
  #lastTime = 0;
  #maxY = 0;

  connectedCallback() {
    const panel = this.querySelector('[popover]');
    if (!panel || this.#alreadyPopped()) return;

    this.#abort = new AbortController();
    const { signal } = this.#abort;
    // Give shoppers a moment on the page before any exit nudge can fire.
    this.#armTimer = window.setTimeout(() => { this.#armed = true; }, ARM_DELAY);

    document.documentElement.addEventListener('mouseleave', (event) => {
      if (event.clientY <= 0) this.#pop(panel);
    }, { signal });

    if (window.matchMedia('(pointer: coarse)').matches) {
      this.#lastY = window.scrollY;
      this.#lastTime = performance.now();
      window.addEventListener('scroll', () => {
        const now = performance.now();
        const y = window.scrollY;
        this.#maxY = Math.max(this.#maxY, y);
        const travelledUp = this.#lastY - y;
        if (now - this.#lastTime > FLICK_TIME) {
          this.#lastY = y;
          this.#lastTime = now;
          return;
        }
        if (travelledUp > FLICK_DISTANCE && this.#maxY > window.innerHeight) this.#pop(panel);
      }, { signal, passive: true });
    }
  }

  disconnectedCallback() {
    this.#abort?.abort();
    window.clearTimeout(this.#armTimer);
  }

  #alreadyPopped() {
    try {
      return window.sessionStorage.getItem(this.dataset.sessionKey) === '1';
    } catch {
      return false;
    }
  }

  #pop(panel) {
    if (!this.#armed || panel.matches(':popover-open')) return;
    // Stay quiet while another overlay (cart drawer, search, menu) is open.
    if (document.querySelector('dialog[open]')) return;
    try {
      window.sessionStorage.setItem(this.dataset.sessionKey, '1');
    } catch {
      // Without storage, the listener removal below still limits it to this page view.
    }
    this.#abort?.abort();
    panel.showPopover?.();
  }
}

if (!customElements.get('s-receipt-bug')) customElements.define('s-receipt-bug', ReceiptBug);
